import { ImageSaasEscrow, ImageContinuity, ImageCustodian, ImageEscrow } from 'src/components/Svg/Images';

import CompanyTaskIcon from '../ui/CompanyTaskIcon';
import AppRequestTaskIcon from '../ui/AppRequestTaskIcon';

import type {
  IPayload,
  IEscrowInvitationSentPayload,
  IInvitationRejectedPayload,
  ICustodianProviderPayload,
  IAssetDisconnectedPayload,
  IContinuityProviderPayload,
  IAppPayload,
  IAppRequestPayload,
} from './types';

import { eventTypeKeys } from './options';

const softwareEscrowEventTypes = ['escrow_signed'] as const;

const continuityEscrowEventTypes = ['escrow_signed', 'escrow_pending_activation'] as const;

const sharedNotificationContent = ({
  eventType,
  payload,
  escrowName,
}: {
  eventType: (typeof eventTypeKeys)[number];
  payload: IPayload;
  escrowName: string;
}) => {
  switch (eventType) {
    case 'escrow_invitation_sent': {
      const { inviting_company_name, company_name, party_type } = payload as IEscrowInvitationSentPayload;
      const escrowName = (
        party_type === 'depositor' ? [company_name, inviting_company_name] : [inviting_company_name, company_name]
      )
        .filter(Boolean)
        .join(' <> ');

      return {
        title: `Respond to your invitation for ${escrowName}`,
        description: `You have been invited to join escrow ${escrowName}. Accept or reject the invitation to let the other party know how to proceed.`,
      };
    }
    case 'escrow_invitation_rejected': {
      const { rejected_company_name } = payload as IInvitationRejectedPayload;

      return {
        title: `Delete escrow ${escrowName}`,
        description: `Your invitation to ${rejected_company_name} was rejected. This escrow cannot proceed - delete it and create a new one to start again.`,
      };
    }
    case 'agreement_sent_for_review_beneficiary':
    case 'agreement_sent_for_review_depositor': {
      return {
        title: `Review your agreement for ${escrowName}`,
        description: 'Codekeeper has sent your escrow agreement for review. Approve it to move forward to signing.',
      };
    }
    case 'agreement_resent_for_review_beneficiary':
    case 'agreement_resent_for_review_depositor': {
      return {
        title: `Re-review your updated agreement for ${escrowName}`,
        description: 'Your escrow agreement has been updated and requires your approval again. Approve it to continue.',
      };
    }
    case 'signatures_requested':
      return {
        title: 'Escrow agreement waiting for signatures',
        description: `The agreement for your escrow ${escrowName} has been sent to all signatories for e-signature. If they haven't received it, ask them to check their spam folder.`,
      };

    case 'suggested_change_created': {
      return {
        title: `Review change request for ${escrowName}`,
        description: `A change request has been submitted for escrow ${escrowName}. Accept or reject it to let the other party know how to proceed.`,
      };
    }
    case 'escrow_termination_requested': {
      return {
        title: `Respond to termination request for ${escrowName}`,
        description: `A termination request has been submitted for escrow ${escrowName}. Accept or reject it to let the other party know how to proceed`,
      };
    }
    case 'custodian_profile_terminated': {
      const { provider_name, name } = payload as ICustodianProviderPayload;

      return {
        title: `Remove Codekeeper's access for ${name}`,
        description: `Custodian profile ${provider_name} has been terminated. Remove Codekeeper's user from all associated accounts to fully close access.`,
      };
    }
    case 'asset_disconnected': {
      const { asset_name } = payload as IAssetDisconnectedPayload;

      return {
        title: `Reconnect sync for ${asset_name}`,
        description: `Asset ${asset_name} sync has been disconnected from escrow ${escrowName}. Deposits will not update until the connection is restored.`,
      };
    }
    case 'continuity_provider_removed': {
      const { provider_name } = payload as IContinuityProviderPayload;

      return {
        title: `Remove Codekeeper's access to ${provider_name}`,
        description: `Provider ${provider_name} has been removed from escrow ${escrowName}. Remove Codekeeper's user from the provider account to close access.`,
      };
    }
    case 'continuity_provider_access_issues': {
      const { provider_name } = payload as IContinuityProviderPayload;

      return {
        title: `Resolve provider access issues for ${escrowName}`,
        description: `Codekeeper detected access issues with provider ${provider_name} in escrow ${escrowName}. Restore access to maintain continuity protection, then reconfirm the provider.`,
      };
    }
    case 'escrow_terminated':
      return {
        title: `Remove Codekeeper's access for ${escrowName}`,
        description: `Escrow ${escrowName} has been terminated. Remove Codekeeper's user from all associated provider accounts to fully close access.`,
      };
    case 'payment_source_expiring':
      return {
        title: 'Update expiring payment method',
        description:
          'Your payment method is about to expire. Update it now to avoid any interruption to your subscription.',
      };
    case 'payment_source_expired':
      return {
        title: 'Replace expired payment method',
        description:
          'Your payment method has expired. Add a new one now to avoid any interruption to your subscription.',
      };
    case 'app_created': {
      const { name } = payload as IAppPayload;

      return {
        title: `${name} has no protection yet`,
        description: 'Add an escrow, backup, or continuity product so Codekeeper can start protecting it.',
      };
    }
    case 'app_request_created': {
      const { requester_company_name } = payload as IAppRequestPayload;

      return {
        title: `Respond to a vendor request from ${requester_company_name}`,
        description: `${requester_company_name} would like you to set up escrow with them.`,
      };
    }
    default:
      return null;
  }
};

const softwareEscrowNotificationContent = (
  eventType: (typeof softwareEscrowEventTypes)[number],
  escrowName: string,
) => {
  switch (eventType) {
    case 'escrow_signed':
      return {
        title: `Deposit assets into ${escrowName}`,
        description:
          'Your escrow agreement is signed. Connect your assets from the Deposits tab to activate your escrow.',
      };
    default:
      return null;
  }
};

const continuityEscrowNotificationContent = (
  eventType: (typeof continuityEscrowEventTypes)[number],
  escrowName: string,
) => {
  switch (eventType) {
    case 'escrow_signed':
      return {
        title: `Set up ${escrowName} environment`,
        description:
          'Your escrow agreement is signed. Add your providers from the Environment tab to activate your escrow.',
      };
    case 'escrow_pending_activation':
      return {
        title: `Follow Codekeeper's activation instructions for ${escrowName}`,
        description: `Codekeeper will send instructions to confirm provider access for ${escrowName}. Follow those instructions to complete activation.`,
      };
    default:
      return null;
  }
};

export const getTaskIcon = (type: string | undefined) => {
  switch (type) {
    case 'SoftwareEscrow':
      return ImageEscrow;
    case 'SaasEscrow':
      return ImageSaasEscrow;
    case 'ContinuityEscrow':
      return ImageContinuity;
    case 'CustodianProfile':
      return ImageCustodian;
    case 'Company':
    case 'App':
      return CompanyTaskIcon;
    default:
      return null;
  }
};

type ITaskIcon = React.ComponentType<{ className?: string }>;

// Event-specific icons take precedence over the parent type icon
const eventTypeIcons: Partial<Record<(typeof eventTypeKeys)[number], ITaskIcon>> = {
  app_request_created: AppRequestTaskIcon,
};

export const getTaskContent = ({
  eventType,
  payload,
  parentType,
}: {
  eventType: (typeof eventTypeKeys)[number];
  payload: IPayload;
  parentType: string;
}): {
  title: string;
  description: string;
  icon: ITaskIcon | null;
} | null => {
  const escrowName = [payload?.depositor_company_name, payload?.beneficiary_company_name].filter(Boolean).join(' <> ');
  const resolvedParentType = parentType === 'Escrow' ? payload.escrow_type : parentType;

  let content: { title: string; description: string } | null;

  if (
    (resolvedParentType === 'SoftwareEscrow' || resolvedParentType === 'SaasEscrow') &&
    softwareEscrowEventTypes.includes(eventType as (typeof softwareEscrowEventTypes)[number])
  ) {
    content = softwareEscrowNotificationContent(eventType as (typeof softwareEscrowEventTypes)[number], escrowName);
  } else if (
    resolvedParentType === 'ContinuityEscrow' &&
    continuityEscrowEventTypes.includes(eventType as (typeof continuityEscrowEventTypes)[number])
  ) {
    content = continuityEscrowNotificationContent(eventType as (typeof continuityEscrowEventTypes)[number], escrowName);
  } else {
    content = sharedNotificationContent({ eventType, payload, escrowName });
  }

  return content && { ...content, icon: eventTypeIcons[eventType] ?? getTaskIcon(resolvedParentType) };
};
